import { exportData } from 'legacy/hpscoutso/components/pages/aplCommon/exportData'
import { TSPRCompany } from 'legacy/lut/classes/models/ISPRSearch'
import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { combineValidateRules } from 'legacy/lut/helpers/combineValidateRules'
import { lookupGetStateOld } from 'legacy/lut/store'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Table } from 'core-components/library/list'
import { IColumn } from 'core-components/library/list/types'
import { Loader } from 'core-components/library/loader'
import { Controller, ValidateInput, ValidationStatus } from 'core-components/library/validation'
import { Logic, Operator, Static } from 'core-components/types'
import { ICountry } from 'core/classes/models/IReference'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'
import { APLTranslationCodes } from 'onboarding/configs/translations/aplTranslationCodes'
import { LanguageContext } from 'onboarding/context/LanguageContext'
import { templateService } from 'onboarding/services/templateService'
import {
  REGEX_COMPANY_INVALID_CHARACTERS,
  REGEX_TAX_INVALID_CHARACTERS,
  REGEX_TRIM_ALL_SPACES,
} from 'src/config/regexp'

import { APLSearchHeader } from './components/aplSearchHeader/APLSearchHeader'

import styles from './APLSearchContent.module.scss'

const composeColumns = (t: (key: APLTranslationCodes) => string): IColumn<TSPRCompany>[] => {
  return [
    {
      title: t(APLTranslationCodes.legal_name),
      field: 'CompanyLegalName',
      hideSettings: true,
      disableSort: true,
    },
    {
      title: t(APLTranslationCodes.communication_name),
      field: 'CompanyCommunicationName',
      hideSettings: true,
      disableSort: true,
    },
    {
      title: t(APLTranslationCodes.country),
      field: 'CompanyCountry',
      hideSettings: true,
      disableSort: true,
    },
    {
      title: t(APLTranslationCodes.search_page_tax_id),
      field: 'CompanyTaxID',
      hideSettings: true,
      disableSort: true,
    },
  ]
}

export function APLSearchContent() {
  const { t } = useContext(LanguageContext)

  const [crud, setCrud] = useState<Crud<TSPRCompany>>(
    CrudStore.get(CrudStoreItemOnboarding.SPRSearch)
  )

  const [isRequiredValidation, setIsRequiredValidation] = useState(false)
  const [companyName, setCompanyName] = useState('')
  const [country, setCountry] = useState('')
  const [taxId, setTaxId] = useState('')
  const [canSearch, setCanSearch] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [availabelCountryCodes, setAvailabelCountryCodes] = useState<Set<string>>(new Set())

  const valueDependencies = useMemo(() => {
    return [country, taxId, companyName]
  }, [country, taxId, companyName])

  const countryCrud = useMemo(() => {
    return new Crud({
      fetcher: new Static<ICountry>(
        lookupGetStateOld()
          .references.countries.filter((item) => availabelCountryCodes.has(item.value))
          .sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1
            }
            return 0
          })
      ),
      keyField: 'name',
      defaultPaging: {
        page: 0,
        pageSize: 100,
      },
    })
  }, [lookupGetStateOld().references.countries, availabelCountryCodes])

  const handleSearch = () => {
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'CompanyLegalName',
          value: companyName.replace(REGEX_TRIM_ALL_SPACES, ''),
          operator: Operator.EQUAL,
        },
        {
          field: 'CompanyCountry',
          value: country,
          operator: Operator.EQUAL,
        },
        {
          field: 'CompanyTaxID',
          value: taxId.replace(REGEX_TRIM_ALL_SPACES, ''),
          operator: Operator.EQUAL,
        },
      ],
    })

    setCrud(crud)
  }

  const handleExport = async () => {
    setExporting(true)
    exportData({ companyName, country, taxId }).finally(() => setExporting(false))
  }

  const isOneOfRequired = async (value: string) => {
    let result = ''
    if (isRequiredValidation) {
      if (!companyName && !country && !taxId) {
        result = 'Required Company Name and Country or TaxID'
      }
    }

    return result
  }

  const checkCompanyName = async (value: string) => {
    let result = ''
    if (isRequiredValidation) {
      if (!taxId && country && !value) {
        result = 'Required'
      }

      if (value && value.trim().length < 3) {
        result = t(APLTranslationCodes.search_company_name_requirements)
      }

      const matched = value.match(REGEX_COMPANY_INVALID_CHARACTERS)
      if (matched) {
        result = prepareErrorMessageFromMatched(matched)
      }
    }

    return result
  }

  const prepareErrorMessageFromMatched = (matched: RegExpMatchArray) => {
    let result = ''
    if (matched) {
      const charsInMatches = matched.reduce((previousValue: string[], group: string) => {
        return [...previousValue, ...Array.from(group)]
      }, [])

      const set = new Set(charsInMatches)
      result = `Should not contains: ${Array.from(set).join(' ')}`
    }

    return result
  }

  const checkCountry = async (value: string) => {
    let result = ''
    if (isRequiredValidation) {
      if (!taxId && companyName && !value) {
        result = 'Required'
      }
    }

    return result
  }

  const checkTaxID = async (value: string) => {
    let result = ''
    if (isRequiredValidation) {
      if (value && value.length < 4) {
        result = t(APLTranslationCodes.search_tax_id_requirements)
      }

      if (!value && ((companyName && !country) || (!companyName && country))) {
        result = 'Required'
      }

      const matched = value.match(REGEX_TAX_INVALID_CHARACTERS)
      if (matched) {
        result = prepareErrorMessageFromMatched(matched)
      }
    }

    return result
  }

  useEffect(() => {
    const getCountryList = async () => {
      const countryCodes = await templateService.fetchAvailableCountriesByRequestType(
        RequestTypes.SimplePartnerRegistration
      )
      setAvailabelCountryCodes(new Set(countryCodes))
    }
    getCountryList()
  }, [])

  return (
    <div className={styles.content}>
      <APLSearchHeader />
      <div className={styles.mainContent}>
        <div>{t(APLTranslationCodes.search_description)}</div>
        <div className={styles.searchArea}>
          <Controller
            onValidationStatusChange={(result) => {
              if (result.status === ValidationStatus.Valid) {
                setCanSearch(true)
              } else if (result.status === ValidationStatus.Invalid) {
                setCanSearch(false)
              }
            }}
          >
            <ValidateInput
              onKeyDown={() => {
                setIsRequiredValidation(true)
              }}
              valueDependencies={valueDependencies}
              classes={{ validatorClasses: { root: styles.searchArea__input } }}
              value={companyName}
              onChange={setCompanyName}
              label={t(APLTranslationCodes.company_name)}
              checkFn={combineValidateRules([checkCompanyName, isOneOfRequired])}
            />
            <Dropdown
              valueDependencies={valueDependencies}
              checkFn={combineValidateRules([checkCountry, isOneOfRequired])}
              onBlur={() => {
                setIsRequiredValidation(true)
              }}
              classes={{ controlClasses: { root: styles.searchArea__input } }}
              renderFunction={(item) => {
                return <div className={styles.djrFont}>{item.name}</div>
              }}
              source={countryCrud}
              value={country}
              caption="name"
              onChange={setCountry}
              label={t(APLTranslationCodes.country)}
            />
            <ValidateInput
              valueDependencies={valueDependencies}
              onKeyDown={() => {
                setIsRequiredValidation(true)
              }}
              classes={{ validatorClasses: { root: styles.searchArea__input } }}
              value={taxId}
              onChange={setTaxId}
              label={t(APLTranslationCodes.search_page_tax_id)}
              checkFn={combineValidateRules([checkTaxID, isOneOfRequired])}
            />
            <Button
              className={styles.searchArea__button}
              onClick={handleSearch}
              disabled={!canSearch || !isRequiredValidation}
            >
              {t(APLTranslationCodes.search)}
            </Button>
          </Controller>
        </div>

        <Table
          rowsPerPageOptions={[
            { key: 5, value: '5' },
            { key: 10, value: '10' },
            { key: 15, value: '15' },
          ]}
          header
          pagination
          source={crud}
          columns={composeColumns(t)}
          classes={{ root: styles.tableContainer }}
        />

        <div className={styles.actions}>
          {exporting ? (
            <Loader className={styles.loaderContainer} dimension={{ width: 45, height: 45 }} />
          ) : (
            <Button
              className={styles.exportBtn}
              onClick={handleExport}
              disabled={!canSearch && !exporting}
            >
              {t(APLTranslationCodes.export)}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
